import { forwardRef, useCallback } from 'react';
import {
    Tab,
    type TabProps,
    colorsPalette,
    shadowElevationsFactory,
    useUiEvent,
} from '@lingoda/ui';
import { actionClick } from '@lingoda/analytics';
import { styled } from '@lingoda/ui/theme';
import { RouterLink, type RouterLinkProps } from '@lingoda/router';
import type { FC, SyntheticEvent } from 'react';

interface CurrentTabProps extends TabProps {
    track?: string;
    sidebar?: boolean;
    onClick?: (e: SyntheticEvent) => void;
}

const SidebarTab = styled(Tab)(({ theme }) => ({
    padding: theme.spacing(2, 3),
    '& .MuiTab-wrapper': {
        alignItems: 'start',
        lineHeight: theme.typography.overline.lineHeight,
    },
}));

const HeaderTab = styled(Tab)(({ theme }) => ({
    fontWeight: 600,
    minWidth: 'auto',
    color: colorsPalette.blackAlpha[70],
    marginRight: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5),
    [theme.breakpoints.only('sm')]: {
        fontSize: theme.typography.subtitle2.fontSize,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },

    '&:not(.Mui-selected):hover': {
        color: colorsPalette.blackSolid[100],
    },

    '&:not(:active):focus': {
        border: '2px solid',
        backgroundColor: colorsPalette.primarySolid[4],
        borderRadius: theme.spacing(0.5),
        boxShadow:
            theme.palette.mode === 'dark'
                ? shadowElevationsFactory.level1(colorsPalette.whiteSolid[100])
                : theme.shadows[1],
        borderColor:
            theme.palette.mode === 'dark'
                ? colorsPalette.whiteAlpha[60]
                : colorsPalette.primaryAlpha[40],
    },
}));

export type LinkTabProps = CurrentTabProps & Omit<RouterLinkProps, 'innerRef'>;

const CollisionLink = forwardRef<HTMLAnchorElement, Omit<RouterLinkProps, 'innerRef'>>(
    (props, ref) => <RouterLink innerRef={ref} {...props} />,
);

CollisionLink.displayName = 'CollisionLink';

export const LinkTab: FC<LinkTabProps> = ({ track, sidebar, onClick, ...props }) => {
    const addEvent = useUiEvent();
    const handleClick = useCallback(
        (e: React.SyntheticEvent) => {
            if (track) {
                void addEvent(actionClick(track));
            }
            if (onClick) {
                onClick(e);
            }

            const currentTarget = e.currentTarget as HTMLAnchorElement;
            // blur the element to prevent leftover border after click on nav item
            currentTarget.blur && currentTarget.blur();
        },
        [track, addEvent, onClick],
    );

    return sidebar ? (
        <SidebarTab disableRipple component={CollisionLink} onClick={handleClick} {...props} />
    ) : (
        <HeaderTab disableRipple component={CollisionLink} onClick={handleClick} {...props} />
    );
};
